import { useCallback, useState } from 'react';
import { formatDateShort, formatDateWithWeekday } from '../../utils/dateHelpers';
import { JourneyPickerCondensed } from './JourneyPickerCondensed';
import { JourneyPickerWrapper } from './JourneyPicker.styles';
import { JourneyPickerDesktop } from './JourneyPickerDesktop';
import { JourneyPickerMobile } from './JourneyPickerMobile';
import type { JourneyPickerProps } from './JourneyPicker.types';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

function buildDatesLabel(
  fromDate: string,
  toDate: string,
  singleMode: boolean,
  selectDate: string,
  selectDates: string
): string {
  if (singleMode) {
    if (!fromDate) return selectDate;
    return formatDateWithWeekday(fromDate);
  }
  if (!fromDate && !toDate) return selectDates;
  if (fromDate && !toDate) return formatDateShort(fromDate);
  if (!fromDate && toDate) return formatDateShort(toDate);

  const fromDay = fromDate.split('-')[2].replace(/^0/, '');
  const to = formatDateShort(toDate);

  const fromMonth = fromDate.slice(5, 7);
  const toMonth = toDate.slice(5, 7);
  if (fromMonth === toMonth && fromDate.slice(0, 4) === toDate.slice(0, 4)) {
    return `${fromDay} - ${to}`;
  }

  return `${formatDateShort(fromDate)} - ${to}`;
}

export function JourneyPicker({
  category: categoryProp,
  defaultCategory,
  categories,
  onCategoryChange,
  disableCategorySelect,
  dateMode = 'range',
  showGuests,
  guestMode = 'split',
  defaultFromDate = '',
  defaultToDate = '',
  defaultAdults = 2,
  defaultChildren = 0,
  maxAdults,
  maxChildren,
  availability,
  isLoadingAvailability,
  showPastDates = false,
  onMonthChange,
  columnView,
  numberOfMonths,
  onSearch,
  labels: l,
  mode = 'default',
  imageOverlay = false,
  showSearchButton = true,
  controlled,
  onEditSection,
  showRate,
  rateCode,
  onRateCodeChange,
}: JourneyPickerProps) {
  const isControlled = !!controlled;

  const [localFromDate, setLocalFromDate] = useState(defaultFromDate);
  const [localToDate, setLocalToDate] = useState(defaultToDate);
  const [localAdults, setLocalAdults] = useState(defaultAdults);
  const [localChildren, setLocalChildren] = useState(defaultChildren);
  const [internalCategory, setInternalCategory] = useState<string | undefined>(defaultCategory);
  const category = categoryProp !== undefined ? categoryProp : internalCategory;

  const fromDate = isControlled ? controlled.fromDate ?? '' : localFromDate;
  const toDate = isControlled ? controlled.toDate ?? '' : localToDate;
  const rawAdults = isControlled ? controlled.adults ?? defaultAdults : localAdults;
  const rawChildren = isControlled ? controlled.children ?? defaultChildren : localChildren;
  const adults = maxAdults !== undefined ? Math.min(rawAdults, maxAdults) : rawAdults;
  const children = maxChildren !== undefined ? Math.min(rawChildren, maxChildren) : rawChildren;

  const isSingleDate = dateMode === 'single';
  const displayGuests = showGuests !== undefined ? showGuests : !isSingleDate;

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      const nextTo = isSingleDate ? '' : checkOut;
      if (isControlled) {
        controlled.onRangeSelect?.(checkIn, nextTo);
        return;
      }
      setLocalFromDate(checkIn);
      setLocalToDate(nextTo);
    },
    [isSingleDate, isControlled, controlled]
  );

  const handleAdultsChange = useCallback(
    (val: number) => {
      if (isControlled) {
        controlled.onAdultsChange?.(val);
        return;
      }
      setLocalAdults(val);
    },
    [isControlled, controlled]
  );

  const handleChildrenChange = useCallback(
    (val: number) => {
      if (isControlled) {
        controlled.onChildrenChange?.(val);
        return;
      }
      setLocalChildren(val);
    },
    [isControlled, controlled]
  );

  const handleCategorySelect = useCallback(
    (id: string) => {
      if (categoryProp === undefined) {
        setInternalCategory(id);
      }
      onCategoryChange?.(id);
    },
    [categoryProp, onCategoryChange]
  );

  const handleSearch = useCallback(() => {
    if (isSingleDate) {
      if (fromDate) onSearch({ fromDate, adults, children });
    } else {
      if (fromDate && toDate) onSearch({ fromDate, toDate, adults, children });
    }
  }, [isSingleDate, fromDate, toDate, adults, children, onSearch]);

  const selectDateLabel = l?.selectDate ?? 'Select date';
  const selectDatesLabel = l?.selectDates ?? 'Select dates';
  const searchLabel = l?.search ?? 'Search';
  const browseLabel = l?.browse ?? 'Browse';
  const closeLabel = l?.close ?? 'Close';
  const defaultGuestsLabel = (total: number) => `${total} Guest${total !== 1 ? 's' : ''}`;

  const datesLabel = buildDatesLabel(
    fromDate,
    toDate,
    isSingleDate,
    selectDateLabel,
    selectDatesLabel
  );
  const guestsLabel = (l?.guests ?? defaultGuestsLabel)(adults + children);

  const showCategory = Boolean(!disableCategorySelect && categories && categories.length > 0);
  const currentCategoryLabel = categories?.find((c) => c.id === category)?.label ?? browseLabel;

  const datesAndGuestsLabel = displayGuests ? `${datesLabel} · ${guestsLabel}` : datesLabel;
  const triggerTitle = showCategory ? currentCategoryLabel : datesAndGuestsLabel;
  const triggerSubtitle = showCategory ? datesAndGuestsLabel : undefined;

  const shared: JourneyPickerSharedProps = {
    showCategory,
    categories,
    category,
    currentCategoryLabel,
    onCategorySelect: handleCategorySelect,
    fromDate,
    toDate,
    isSingleDate,
    datesLabel,
    onRangeSelect: handleRangeSelect,
    displayGuests,
    guestMode,
    adults,
    children,
    maxAdults,
    maxChildren,
    guestsLabel,
    onAdultsChange: handleAdultsChange,
    onChildrenChange: handleChildrenChange,
    availability,
    isLoadingAvailability,
    showPastDates,
    onMonthChange,
    onSearch: handleSearch,
    searchLabel,
    imageOverlay,
    datePickerLabels: l?.datePicker,
    guestPickerLabels: l?.guestPicker,
    showRate,
    rateCode,
    rateLabels: {
      pill: l?.rateLabels?.pill ?? 'Rate',
      placeholder: l?.rateLabels?.placeholder ?? 'Rate code',
      apply: l?.rateLabels?.apply ?? 'Apply',
      clear: l?.rateLabels?.clear ?? 'Clear rate code',
    },
    onRateCodeChange,
  };

  if (mode === 'condensed') {
    return (
      <JourneyPickerCondensed
        showCategory={showCategory}
        currentCategoryLabel={currentCategoryLabel}
        datesLabel={datesLabel}
        displayGuests={displayGuests}
        guestsLabel={guestsLabel}
        onEditSection={onEditSection}
      />
    );
  }

  return (
    <JourneyPickerWrapper>
      <JourneyPickerMobile
        {...shared}
        triggerTitle={triggerTitle}
        triggerSubtitle={triggerSubtitle}
        closeLabel={closeLabel}
      />

      <JourneyPickerDesktop
        {...shared}
        showSearchButton={showSearchButton}
        columnView={columnView}
        numberOfMonths={numberOfMonths}
      />
    </JourneyPickerWrapper>
  );
}
